

// Use global logger to avoid circular dependencies
const log = typeof window !== 'undefined' && window.Log ? window.Log('errorBoundary') : {
  blue: () => {}, red: () => {}, green: () => {}, yellow: () => {}, purple: () => {}, log: () => {}, table: () => {}
};

/**
 * Global Error Boundary - Catches unhandled errors and provides user feedback
 */
export class ErrorBoundary {
  constructor() {
    this.errorCount = 0;
    this.maxErrors = 10; // Prevent error spam
    this.errorLog = [];
    log.green('🛡️ Error Boundary initializing...');
    this.setupGlobalErrorHandlers();
    log.green('✅ Error Boundary active and listening for errors');
  }

  setupGlobalErrorHandlers() {
    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      log.yellow('🚨 Caught JavaScript error:', event);
      this.handleError({
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        error: event.error,
        type: 'javascript'
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      log.yellow('🚨 Caught promise rejection:', event);
      this.handleError({
        message: event.reason?.message || 'Unhandled promise rejection',
        error: event.reason,
        type: 'promise'
      });
      
      // Prevent browser's default unhandled rejection behavior
      event.preventDefault();
    });

    // Handle custom application errors
    window.addEventListener('app-error', (event) => {
      this.handleError({
        message: event.detail.message,
        error: event.detail.error,
        component: event.detail.component,
        type: 'application'
      });
    });
    
    // Handle component-specific errors (for consistency)
    window.addEventListener('component-error', (event) => {
      this.handleError({
        message: event.detail.error?.message || 'Component error',
        error: event.detail.error,
        component: event.detail.component,
        type: 'component',
        context: event.detail.context
      });
    });
  }

  handleError(errorInfo) {
    this.errorCount++;
    
    // Prevent error spam
    if (this.errorCount > this.maxErrors) {
      log.red('Too many errors, suppressing further error handling');
      return;
    }

    // Log detailed error information
    log.red('Error boundary caught error:', errorInfo);
    
    // Store error for potential reporting
    this.errorLog.push({
      ...errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Show user-friendly error message
    this.showErrorToast(errorInfo);

    // Report error to console with stack trace
    if (errorInfo.error) {
      console.error('Error Details:', errorInfo.error);
    }

    // In development, you might want to show more details
    if (this.isDevelopment()) {
      this.showDeveloperError(errorInfo);
    }
  }

  showErrorToast(errorInfo) {
    const toast = document.createElement('div');
    toast.className = 'toast error-toast';
    
    const icon = document.createElement('span');
    icon.className = 'error-icon';
    icon.innerHTML = '⚠️';
    
    const message = document.createElement('span');
    message.textContent = this.getUserFriendlyMessage(errorInfo);
    
    const dismissButton = document.createElement('button');
    dismissButton.className = 'toast-dismiss';
    dismissButton.innerHTML = '✕';
    dismissButton.addEventListener('click', () => toast.remove());
    
    toast.appendChild(icon);
    toast.appendChild(message);
    toast.appendChild(dismissButton);
    
    document.body.appendChild(toast);
    
    // Auto-dismiss after 8 seconds (longer for errors)
    setTimeout(() => {
      if (toast.parentNode) {
        toast.remove();
      }
    }, 8000);
  }

  getUserFriendlyMessage(errorInfo) {
    // In production, use more generic messages to avoid exposing technical details
    const isProduction = !this.isDevelopment();
    
    switch (errorInfo.type) {
      case 'network':
        return 'Network error - please check your connection and try again';
      case 'api':
        return 'Server error - please try again in a moment';
      case 'promise':
        return 'An unexpected error occurred - the page may need to be refreshed';
      case 'component':
        // In production, don't expose component names or technical details
        return isProduction 
          ? 'Some features may not be working correctly - please refresh the page'
          : `Error in ${errorInfo.component || 'component'} - some features may not work correctly`;
      default:
        return 'An unexpected error occurred - please refresh the page if problems persist';
    }
  }

  showDeveloperError(errorInfo) {
    // Only show in development
    if (!this.isDevelopment()) return;

    console.group('🐛 Error Boundary - Developer Info');
    console.error('Error Type:', errorInfo.type);
    console.error('Component:', errorInfo.component || 'Unknown');
    console.error('Message:', errorInfo.message);
    if (errorInfo.filename) {
      console.error('File:', `${errorInfo.filename}:${errorInfo.line}:${errorInfo.column}`);
    }
    console.error('Stack:', errorInfo.error?.stack);
    console.error('Full Error Info:', errorInfo);
    console.groupEnd();
  }

  isDevelopment() {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.protocol === 'file:';
  }

  // Method for components to report errors
  static reportError(error, component = 'Unknown') {
    const errorEvent = new CustomEvent('app-error', {
      detail: {
        message: error.message || 'Unknown error',
        error: error,
        component: component
      }
    });
    window.dispatchEvent(errorEvent);
  }

  // Get error statistics
  getErrorStats() {
    return {
      totalErrors: this.errorCount,
      recentErrors: this.errorLog.slice(-5), // Last 5 errors
      errorTypes: this.errorLog.reduce((acc, error) => {
        acc[error.type] = (acc[error.type] || 0) + 1;
        return acc;
      }, {})
    };
  }

  // Reset error count (useful for testing)
  reset() {
    this.errorCount = 0;
    this.errorLog = [];
  }
}

// Create and export singleton instance
export const errorBoundary = new ErrorBoundary();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.errorBoundary = errorBoundary;
  window.ErrorBoundary = ErrorBoundary;
  
  // Add testing helpers that will properly trigger the error boundary
  window.testErrorBoundary = {
    jsError: () => {
      setTimeout(() => {
        throw new Error('Test JavaScript error from testErrorBoundary');
      }, 0);
    },
    promiseError: () => {
      Promise.reject(new Error('Test promise rejection from testErrorBoundary'));
    },
    customError: () => {
      ErrorBoundary.reportError(new Error('Test custom app error'), 'TestHelper');
    },
    stats: () => {
      return window.errorBoundary.getErrorStats();
    }
  };
}
